import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiClient } from '@packages/api-client';
import { router } from '../router';
import { isEmbedMode, handleEmbedUnauthorized } from '../hooks/useParentAuthSync';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // 优先检查 localStorage 中是否已有 token（模块级 initUrlAuth 已同步写入）
    if (apiClient.isAuthenticated()) return true;
    // 防护：如果是嵌入模式且 URL 中携带 token，视为已认证（token 正在同步写入）
    const params = new URLSearchParams(window.location.search);
    if (params.get('token') && isEmbedMode()) return true;
    return false;
  });

  // 监听认证状态变化（由 URL token 初始化触发）
  useEffect(() => {
    const handleParentSync = () => {
      setIsAuthenticated(apiClient.isAuthenticated());
    };

    window.addEventListener('auth:parent-sync', handleParentSync);
    return () => {
      window.removeEventListener('auth:parent-sync', handleParentSync);
    };
  }, []);

  // 监听认证失效事件
  useEffect(() => {
    const handleUnauthorized = () => {
      setIsAuthenticated(false);

      if (isEmbedMode()) {
        // 嵌入模式下，通知父项目处理重定向
        handleEmbedUnauthorized();
        return;
      }

      // 非嵌入模式：如果在非登录页面，重定向到登录页
      if (location.pathname !== '/login') {
        const currentPath = `${location.pathname}${location.search}`;
        router.navigate(
          `/login?redirect=${encodeURIComponent(currentPath)}`,
          { replace: true }
        );
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:embed-unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:embed-unauthorized', handleUnauthorized);
    };
  }, [location.pathname, location.search]);

  if (!isAuthenticated) {
    // 嵌入模式下仅通知父项目，由父项目处理重定向
    if (isEmbedMode()) {
      handleEmbedUnauthorized();
      // 显示空白状态，等待父项目重定向
      return <div className="h-full w-full" />;
    }

    // 非嵌入模式：重定向到登录页
    const currentPath = `${location.pathname}${location.search}`;
    const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
    return <Navigate to={loginUrl} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
